/**
 * AxLabs Mathematics Experiments - Calculus
 *
 * Collection of experiments exploring calculus concepts.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// LIMIT EPSILON-DELTA APPROACH
// ============================================================================
export const LimitEpsilonDeltaApproach = createExperimentFromSchema({
    id: 'limit-epsilon-delta-approach',
    name: 'Limit Epsilon-Delta Approach',
    domain: 'mathematics',
    description: 'Visualize the epsilon-delta definition of a limit',
    conceptTags: ['Limits', 'Epsilon-Delta', 'Continuity', 'Formal Definition'],
    difficulty: 6,
    stateVariables: [
        { id: 'epsilon', name: 'Epsilon ε', unit: 'dimensionless', initial: 0.5, symbol: '\\epsilon' },
        { id: 'delta', name: 'Delta δ', unit: 'dimensionless', initial: 0.25, symbol: '\\delta' },
        { id: 'c', name: 'Point c', unit: 'dimensionless', initial: 2, symbol: 'c' },
        { id: 'L', name: 'Limit L', unit: 'dimensionless', initial: 4, symbol: 'L' },
        { id: 'satisfied', name: 'Condition Met', unit: 'dimensionless', initial: 1, symbol: 'S' },
    ],
    parameters: [
        { id: 'epsilonTarget', name: 'Target Epsilon', unit: 'dimensionless', min: 0.1, max: 2, default: 0.5, symbol: '\\epsilon' },
        { id: 'cTarget', name: 'Point c', unit: 'dimensionless', min: -3, max: 3, default: 2, symbol: 'c' },
    ],
    equations: [
        {
            id: 'epsilon-delta',
            name: 'Epsilon-Delta Definition',
            latex: '\\lim_{x\\to c}f(x) = L \\iff \\forall\\epsilon>0, \\exists\\delta>0: 0<|x-c|<\\delta \\implies |f(x)-L|<\\epsilon',
            description: 'Formal definition of limit',
        },
        {
            id: 'limit-exists',
            name: 'Limit Condition',
            latex: '|f(x) - L| < \\epsilon \\text{ whenever } 0 < |x - c| < \\delta',
            description: 'Function values within epsilon of limit when x within delta of c',
        },
    ],
    derivatives: (_state, params) => {
        const epsilonTarget = getParam(params, 'epsilonTarget');
        const cTarget = getParam(params, 'cTarget');
        // For f(x) = x², limit as x→c is c²
        const L = cTarget * cTarget;
        // Find delta such that |x² - c²| < epsilon when |x - c| < delta
        // |x² - c²| = |x - c||x + c|
        // For x near c, |x + c| ≈ 2|c|
        // delta = epsilon / (2|c| + 1) to be safe
        const delta = cTarget !== 0 ? epsilonTarget / (2 * Math.abs(cTarget) + 1) : Math.sqrt(epsilonTarget);
        const satisfied = 1; // By construction
        return {
            epsilon: epsilonTarget - getState(_state, 'epsilon'),
            delta: delta - getState(_state, 'delta'),
            c: cTarget - getState(_state, 'c'),
            L: L - getState(_state, 'L'),
            satisfied: satisfied - getState(_state, 'satisfied'),
        };
    },
    bounds: {
        epsilon: { min: 0, max: 5 },
        delta: { min: 0, max: 5 },
        L: { min: 0, max: 25 },
    },
});
// ============================================================================
// DERIVATIVE AS SLOPE
// ============================================================================
export const DerivativeAsSlope = createExperimentFromSchema({
    id: 'derivative-as-slope',
    name: 'Derivative as Slope',
    domain: 'mathematics',
    description: 'Visualize derivative as slope of tangent line',
    conceptTags: ['Derivative', 'Tangent Line', 'Slope', 'Rate of Change'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Point x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 1, symbol: 'f(x)' },
        { id: 'slope', name: 'f\'(x)', unit: 'dimensionless', initial: 2, symbol: 'f\'(x)' },
        { id: 'tangentY', name: 'Tangent at x+1', unit: 'dimensionless', initial: 3, symbol: 'y_{tan}' },
        { id: 'secantSlope', name: 'Secant Slope', unit: 'dimensionless', initial: 2.01, symbol: 'm_{sec}' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'h', name: 'Δx for Secant', unit: 'dimensionless', min: 0.001, max: 1, default: 0.1, symbol: 'h' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'derivative-def',
            name: 'Derivative Definition',
            latex: 'f\'(x) = \\lim_{h\\to 0}\\frac{f(x+h) - f(x)}{h}',
            description: 'Derivative as limit of difference quotient',
        },
        {
            id: 'tangent-line',
            name: 'Tangent Line Equation',
            latex: 'y = f\'(a)(x - a) + f(a)',
            description: 'Line tangent to curve at point a',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const h = getParam(params, 'h');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        let slope = 0;
        let derivative = 0;
        switch (funcType) {
            case 0: // f(x) = x²
                y = nextX * nextX;
                derivative = 2 * nextX;
                break;
            case 1: // f(x) = x³
                y = nextX * nextX * nextX;
                derivative = 3 * nextX * nextX;
                break;
            case 2: // f(x) = sin(x)
                y = Math.sin(nextX);
                derivative = Math.cos(nextX);
                break;
            case 3: // f(x) = exp(x)
                y = Math.exp(nextX);
                derivative = Math.exp(nextX);
                break;
        }
        // Secant slope approximation
        let y2 = 0;
        const x2 = nextX + h;
        switch (funcType) {
            case 0:
                y2 = x2 * x2;
                break;
            case 1:
                y2 = x2 * x2 * x2;
                break;
            case 2:
                y2 = Math.sin(x2);
                break;
            case 3:
                y2 = Math.exp(x2);
                break;
        }
        const secantSlope = (y2 - y) / h;
        const tangentY = slope * (nextX + 1 - nextX) + y;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            slope: derivative - getState(state, 'slope'),
            tangentY: tangentY - getState(state, 'tangentY'),
            secantSlope: secantSlope - getState(state, 'secantSlope'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        y: { min: -10, max: 50 },
        slope: { min: -20, max: 50 },
    },
});
// ============================================================================
// CHAIN RULE VERIFICATION
// ============================================================================
export const ChainRuleVerification = createExperimentFromSchema({
    id: 'chain-rule-verification',
    name: 'Chain Rule Verification',
    domain: 'mathematics',
    description: 'Verify the chain rule for composite functions',
    conceptTags: ['Chain Rule', 'Composite Functions', 'Derivative Rules', 'Differentiation'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'u', name: 'Inner u(x)', unit: 'dimensionless', initial: 2, symbol: 'u(x)' },
        { id: 'chainResult', name: '(f∘g)\' direct', unit: 'dimensionless', initial: 8, symbol: '\\frac{d}{dx}f(g(x))' },
        { id: 'ruleResult', name: 'f\'(g)·g\'', unit: 'dimensionless', initial: 8, symbol: 'f\'(g(x))g\'(x)' },
        { id: 'difference', name: 'Difference', unit: 'dimensionless', initial: 0, symbol: 'Difference' },
    ],
    parameters: [
        { id: 'outer', name: 'Outer f(u)', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'f' },
        { id: 'inner', name: 'Inner g(x)', unit: 'dimensionless', min: 0, max: 2, default: 1, symbol: 'g' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'chain-rule',
            name: 'Chain Rule',
            latex: '\\frac{d}{dx}[f(g(x))] = f\'(g(x)) \\cdot g\'(x)',
            description: 'Derivative of composite function',
        },
        {
            id: 'leibniz-chain',
            name: 'Leibniz Notation',
            latex: '\\frac{dy}{dx} = \\frac{dy}{du} \\cdot \\frac{du}{dx}',
            description: 'Chain rule in Leibniz notation',
        },
    ],
    derivatives: (state, params) => {
        const outer = Math.round(getParam(params, 'outer'));
        const inner = Math.round(getParam(params, 'inner'));
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        // Define inner functions g(x)
        let g = 0;
        let gPrime = 0;
        switch (inner) {
            case 0: // x²
                g = nextX * nextX;
                gPrime = 2 * nextX;
                break;
            case 1: // 2x + 1
                g = 2 * nextX + 1;
                gPrime = 2;
                break;
            case 2: // sin(x)
                g = Math.sin(nextX);
                gPrime = Math.cos(nextX);
                break;
        }
        // Define outer functions f(u) and their derivatives
        let fPrime = 0;
        switch (outer) {
            case 0: // u²
                fPrime = 2 * g;
                break;
            case 1: // √u
                fPrime = 1 / (2 * Math.sqrt(Math.abs(g)));
                break;
            case 2: // sin(u)
                fPrime = Math.cos(g);
                break;
        }
        const ruleResult = fPrime * gPrime;
        // Direct computation via numerical differentiation
        const h = 0.0001;
        let fDirect = 0;
        let fDirectH = 0;
        const xH = nextX + h;
        let gH = 0;
        switch (inner) {
            case 0:
                g = xH * xH;
                break;
            case 1:
                g = 2 * xH + 1;
                break;
            case 2:
                g = Math.sin(xH);
                break;
        }
        switch (outer) {
            case 0:
                fDirect = g * g;
                fDirectH = gH * gH;
                break;
            case 1:
                fDirect = Math.sqrt(Math.abs(g));
                fDirectH = Math.sqrt(Math.abs(gH));
                break;
            case 2:
                fDirect = Math.sin(g);
                fDirectH = Math.sin(gH);
                break;
        }
        const chainResult = (fDirectH - fDirect) / h;
        const difference = Math.abs(chainResult - ruleResult);
        return {
            x: scanRate * 0.016,
            u: g - getState(state, 'u'),
            chainResult: chainResult - getState(state, 'chainResult'),
            ruleResult: ruleResult - getState(state, 'ruleResult'),
            difference: difference - getState(state, 'difference'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        chainResult: { min: -50, max: 50 },
    },
});
// ============================================================================
// OPTIMIZATION CRITICAL POINTS
// ============================================================================
export const OptimizationCriticalPoints = createExperimentFromSchema({
    id: 'optimization-critical-points',
    name: 'Optimization Critical Points',
    domain: 'mathematics',
    description: 'Find critical points and classify extrema',
    conceptTags: ['Critical Points', 'Maxima', 'Minima', 'Second Derivative Test'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Position x', unit: 'dimensionless', initial: -2, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 0, symbol: 'f(x)' },
        { id: 'fp', name: 'f\'(x)', unit: 'dimensionless', initial: 0, symbol: 'f\'(x)' },
        { id: 'fpp', name: 'f\'\'(x)', unit: 'dimensionless', initial: 4, symbol: 'f\'\'(x)' },
        { id: 'critType', name: 'Critical Type', unit: 'dimensionless', initial: 1, symbol: 'Type' },
        { id: 'absMin', name: 'Abs Minimum', unit: 'dimensionless', initial: -4, symbol: 'f_{min}' },
        { id: 'absMax', name: 'Abs Maximum', unit: 'dimensionless', initial: 4, symbol: 'f_{max}' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.03, symbol: 'v' },
    ],
    equations: [
        {
            id: 'critical-point-def',
            name: 'Critical Point Definition',
            latex: 'f\'(x) = 0 \\text{ or undefined}',
            description: 'Critical points where derivative is zero or undefined',
        },
        {
            id: 'second-derivative-test',
            name: 'Second Derivative Test',
            latex: 'f\'\'(c) > 0 \\Rightarrow \\text{min}, \\quad f\'\'(c) < 0 \\Rightarrow \\text{max}',
            description: 'Classify critical points using second derivative',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        let fp = 0;
        let fpp = 0;
        switch (funcType) {
            case 0: // f(x) = x³ - 3x
                y = nextX * nextX * nextX - 3 * nextX;
                fp = 3 * nextX * nextX - 3;
                fpp = 6 * nextX;
                break;
            case 1: // f(x) = x⁴ - 2x²
                y = nextX * nextX * nextX * nextX - 2 * nextX * nextX;
                fp = 4 * nextX * nextX * nextX - 4 * nextX;
                fpp = 12 * nextX * nextX - 4;
                break;
            case 2: // f(x) = sin(x) on [0, 2π]
                y = Math.sin(nextX);
                fp = Math.cos(nextX);
                fpp = -Math.sin(nextX);
                break;
            case 3: // f(x) = x + 1/x
                y = nextX + 1 / nextX;
                fp = 1 - 1 / (nextX * nextX);
                fpp = 2 / (nextX * nextX * nextX);
                break;
        }
        // Classify critical point
        let critType = 0; // 0: neither, 1: min, 2: max
        if (Math.abs(fp) < 0.1) {
            if (fpp > 0.1)
                critType = 1;
            else if (fpp < -0.1)
                critType = 2;
        }
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            fp: fp - getState(state, 'fp'),
            fpp: fpp - getState(state, 'fpp'),
            critType: critType - getState(state, 'critType'),
            absMin: 0,
            absMax: 0,
        };
    },
    bounds: {
        x: { min: -3, max: 3 },
        y: { min: -10, max: 20 },
    },
});
// ============================================================================
// DEFINITE INTEGRAL AS AREA
// ============================================================================
export const DefiniteIntegralAsArea = createExperimentFromSchema({
    id: 'definite-integral-as-area',
    name: 'Definite Integral as Area',
    domain: 'mathematics',
    description: 'Visualize definite integral as area under curve',
    conceptTags: ['Definite Integral', 'Area Under Curve', 'Riemann Sum', 'Accumulation'],
    difficulty: 3,
    stateVariables: [
        { id: 'a', name: 'Lower bound a', unit: 'dimensionless', initial: 0, symbol: 'a' },
        { id: 'b', name: 'Upper bound b', unit: 'dimensionless', initial: 2, symbol: 'b' },
        { id: 'integral', name: '∫ᵇₐ f(x)dx', unit: 'dimensionless', initial: 2.67, symbol: 'I' },
        { id: 'rectangles', name: 'Riemann Sum', unit: 'dimensionless', initial: 2.5, symbol: 'R_n' },
        { id: 'error', name: '|I - Rₙ|', unit: 'dimensionless', initial: 0.17, symbol: 'Error' },
        { id: 'n', name: 'Rectangles n', unit: 'dimensionless', initial: 4, symbol: 'n' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'f' },
        { id: 'nTarget', name: 'Number of Rectangles', unit: 'dimensionless', min: 2, max: 50, default: 10, symbol: 'n' },
        { id: 'upper', name: 'Upper bound b', unit: 'dimensionless', min: 1, max: 5, default: 2, symbol: 'b' },
    ],
    equations: [
        {
            id: 'def-integral',
            name: 'Definite Integral',
            latex: '\\int_a^b f(x)\\,dx = F(b) - F(a)',
            description: 'Integral equals difference of antiderivative at bounds',
        },
        {
            id: 'riemann-sum',
            name: 'Riemann Sum',
            latex: '\\int_a^b f(x)\\,dx = \\lim_{n\\to\\infty}\\sum_{i=1}^n f(x_i^*)\\Delta x',
            description: 'Limit of sums of rectangle areas',
        },
    ],
    derivatives: (_state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const nTarget = Math.round(getParam(params, 'nTarget'));
        const upper = getParam(params, 'upper');
        const a = 0;
        // Compute exact integral
        let integral = 0;
        switch (funcType) {
            case 0: // f(x) = x²
                integral = (upper * upper * upper) / 3;
                break;
            case 1: // f(x) = √x
                integral = (2 / 3) * upper * Math.sqrt(upper);
                break;
            case 2: // f(x) = sin(x)
                integral = 1 - Math.cos(upper);
                break;
        }
        // Compute Riemann sum (right endpoint)
        let rectangles = 0;
        const delta = (upper - a) / nTarget;
        for (let i = 0; i < nTarget; i++) {
            const x = a + (i + 1) * delta;
            let fx = 0;
            switch (funcType) {
                case 0:
                    fx = x * x;
                    break;
                case 1:
                    fx = Math.sqrt(Math.max(0, x));
                    break;
                case 2:
                    fx = Math.sin(x);
                    break;
            }
            rectangles += fx * delta;
        }
        const error = Math.abs(integral - rectangles);
        return {
            a: 0,
            b: upper - getState(_state, 'b'),
            integral: integral - getState(_state, 'integral'),
            rectangles: rectangles - getState(_state, 'rectangles'),
            error: error - getState(_state, 'error'),
            n: nTarget - getState(_state, 'n'),
        };
    },
    bounds: {
        integral: { min: 0, max: 50 },
        rectangles: { min: 0, max: 50 },
    },
});
// ============================================================================
// FUNDAMENTAL THEOREM CONNECTION
// ============================================================================
export const FundamentalTheoremConnection = createExperimentFromSchema({
    id: 'fundamental-theorem-connection',
    name: 'Fundamental Theorem Connection',
    domain: 'mathematics',
    description: 'Explore the connection between derivatives and integrals',
    conceptTags: ['FTC', 'Antiderivative', 'Accumulation Function', 'Derivative of Integral'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Upper bound x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'f', name: 'f(t)', unit: 'dimensionless', initial: 2, symbol: 'f(t)' },
        { id: 'F', name: 'F(x) = ∫₀ˣ f(t)dt', unit: 'dimensionless', initial: 1, symbol: 'F(x)' },
        { id: 'FPrime', name: 'F\'(x)', unit: 'dimensionless', initial: 1, symbol: 'F\'(x)' },
        { id: 'verified', name: 'F\'(x) = f(x)', unit: 'dimensionless', initial: 1, symbol: '✓' },
    ],
    parameters: [
        { id: 'funcType', name: 'f(t) Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'ftc-part1',
            name: 'FTC Part 1',
            latex: '\\frac{d}{dx}\\int_a^x f(t)\\,dt = f(x)',
            description: 'Derivative of integral equals original function',
        },
        {
            id: 'ftc-part2',
            name: 'FTC Part 2',
            latex: '\\int_a^b f(x)\\,dx = F(b) - F(a), \\quad F\' = f',
            description: 'Integral evaluated using antiderivative',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let f = 0;
        let F = 0;
        let FPrime = 0;
        switch (funcType) {
            case 0: // f(t) = 2t, F(x) = x²
                f = 2 * nextX;
                F = nextX * nextX;
                FPrime = 2 * nextX;
                break;
            case 1: // f(t) = e^t, F(x) = e^x - 1
                f = Math.exp(nextX);
                F = Math.exp(nextX) - 1;
                FPrime = Math.exp(nextX);
                break;
            case 2: // f(t) = cos(t), F(x) = sin(x)
                f = Math.cos(nextX);
                F = Math.sin(nextX);
                FPrime = Math.cos(nextX);
                break;
            case 3: // f(t) = 1/(1+t²), F(x) = arctan(x)
                f = 1 / (1 + nextX * nextX);
                F = Math.atan(nextX);
                FPrime = 1 / (1 + nextX * nextX);
                break;
        }
        const verified = Math.abs(FPrime - f) < 0.001 ? 1 : 0;
        return {
            x: scanRate * 0.016,
            f: f - getState(state, 'f'),
            F: F - getState(state, 'F'),
            FPrime: FPrime - getState(state, 'FPrime'),
            verified: verified - getState(state, 'verified'),
        };
    },
    bounds: {
        x: { min: 0, max: 10 },
        F: { min: 0, max: 100 },
    },
});
// ============================================================================
// RELATED RATES INVESTIGATION
// ============================================================================
export const RelatedRatesInvestigation = createExperimentFromSchema({
    id: 'related-rates-investigation',
    name: 'Related Rates Investigation',
    domain: 'mathematics',
    description: 'Solve related rates problems',
    conceptTags: ['Related Rates', 'Chain Rule Applications', 'Implicit Differentiation', 'Word Problems'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Variable x', unit: 'dimensionless', initial: 3, symbol: 'x' },
        { id: 'y', name: 'Variable y', unit: 'dimensionless', initial: 4, symbol: 'y' },
        { id: 'dxdt', name: 'dx/dt', unit: 'dimensionless/s', initial: 0.1, symbol: '\\frac{dx}{dt}' },
        { id: 'dydt', name: 'dy/dt', unit: 'dimensionless/s', initial: -0.075, symbol: '\\frac{dy}{dt}' },
        { id: 'constraint', name: 'Constraint Value', unit: 'dimensionless', initial: 25, symbol: 'x^2+y^2' },
    ],
    parameters: [
        { id: 'problemType', name: 'Problem Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'P' },
        { id: 'rateInput', name: 'Input Rate', unit: 'dimensionless/s', min: -2, max: 2, default: 0.5, symbol: 'r' },
    ],
    equations: [
        {
            id: 'related-rates-chain',
            name: 'Related Rates via Chain Rule',
            latex: '\\frac{dy}{dt} = \\frac{dy}{dx} \\cdot \\frac{dx}{dt}',
            description: 'Relate rates through chain rule',
        },
        {
            id: 'implicit-diff',
            name: 'Implicit Differentiation',
            latex: '\\frac{d}{dt}[f(x,y)] = \\frac{\\partial f}{\\partial x}\\frac{dx}{dt} + \\frac{\\partial f}{\\partial y}\\frac{dy}{dt}',
            description: 'Differentiate constraint implicitly with respect to time',
        },
    ],
    derivatives: (state, params) => {
        const problemType = Math.round(getParam(params, 'problemType'));
        const rateInput = getParam(params, 'rateInput');
        const x = getState(state, 'x');
        const nextX = x + rateInput * 0.016;
        let y = 0;
        let dxdt = rateInput;
        let dydt = 0;
        let constraint = 0;
        switch (problemType) {
            case 0: // x² + y² = 25 (circle)
                y = Math.sqrt(Math.max(0, 25 - nextX * nextX));
                constraint = 25;
                // 2x(dx/dt) + 2y(dy/dt) = 0
                // dy/dt = -(x/y)(dx/dt)
                dydt = y > 0 ? -(nextX / y) * dxdt : 0;
                break;
            case 1: // xy = 12 (hyperbola)
                y = 12 / nextX;
                constraint = 12;
                // (dx/dt)y + x(dy/dt) = 0
                // dy/dt = -(y/x)(dx/dt)
                dydt = -(y / nextX) * dxdt;
                break;
            case 2: // Similar triangles: ladder sliding
                // x² + y² = L², ladder length L
                const L = 5;
                y = Math.sqrt(Math.max(0, L * L - nextX * nextX));
                constraint = L * L;
                dydt = y > 0 ? -(nextX / y) * dxdt : 0;
                break;
        }
        return {
            x: nextX - x,
            y: y - getState(state, 'y'),
            dxdt: dxdt - getState(state, 'dxdt'),
            dydt: dydt - getState(state, 'dydt'),
            constraint: constraint - getState(state, 'constraint'),
        };
    },
    bounds: {
        x: { min: 0, max: 10 },
        y: { min: 0, max: 10 },
        dxdt: { min: -5, max: 5 },
        dydt: { min: -5, max: 5 },
    },
});
// ============================================================================
// TAYLOR SERIES APPROXIMATION
// ============================================================================
export const TaylorSeriesApproximation = createExperimentFromSchema({
    id: 'taylor-series-approximation',
    name: 'Taylor Series Approximation',
    domain: 'mathematics',
    description: 'Approximate functions using Taylor series',
    conceptTags: ['Taylor Series', 'Power Series', 'Approximation', 'Convergence'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Point x', unit: 'dimensionless', initial: 0.5, symbol: 'x' },
        { id: 'actual', name: 'f(x) actual', unit: 'dimensionless', initial: 1.65, symbol: 'f(x)' },
        { id: 'approx1', name: '1st order', unit: 'dimensionless', initial: 1.5, symbol: 'T_1' },
        { id: 'approx2', name: '2nd order', unit: 'dimensionless', initial: 1.625, symbol: 'T_2' },
        { id: 'approx3', name: '3rd order', unit: 'dimensionless', initial: 1.646, symbol: 'T_3' },
        { id: 'error3', name: 'Error |T₃ - f|', unit: 'dimensionless', initial: 0.004, symbol: 'E_3' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'center', name: 'Center a', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'a' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'taylor-series',
            name: 'Taylor Series Formula',
            latex: 'f(x) = \\sum_{n=0}^\\infty \\frac{f^{(n)}(a)}{n!}(x-a)^n',
            description: 'Approximate function near point a using derivatives',
        },
        {
            id: 'maclaurin',
            name: 'Maclaurin Series (a=0)',
            latex: 'f(x) = f(0) + f\'(0)x + \\frac{f\'\'(0)}{2!}x^2 + \\cdots',
            description: 'Taylor series centered at 0',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const a = getParam(params, 'center');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let actual = 0;
        let approx1 = 0;
        let approx2 = 0;
        let approx3 = 0;
        const h = nextX - a;
        switch (funcType) {
            case 0: // e^x at a=0
                actual = Math.exp(nextX);
                approx1 = 1 + h;
                approx2 = 1 + h + h * h / 2;
                approx3 = 1 + h + h * h / 2 + h * h * h / 6;
                break;
            case 1: // sin(x) at a=0
                actual = Math.sin(nextX);
                approx1 = h;
                approx2 = h;
                approx3 = h - h * h * h / 6;
                break;
            case 2: // ln(x) at a=1
                actual = Math.log(Math.max(0.01, nextX));
                const h1 = nextX - 1;
                approx1 = h1;
                approx2 = h1 - h1 * h1 / 2;
                approx3 = h1 - h1 * h1 / 2 + h1 * h1 * h1 / 3;
                break;
            case 3: // sqrt(x) at a=1
                actual = Math.sqrt(Math.max(0, nextX));
                const h2 = nextX - 1;
                approx1 = 1 + h2 / 2;
                approx2 = 1 + h2 / 2 - h2 * h2 / 8;
                approx3 = 1 + h2 / 2 - h2 * h2 / 8 + h2 * h2 * h2 / 16;
                break;
        }
        const error3 = Math.abs(actual - approx3);
        return {
            x: scanRate * 0.016,
            actual: actual - getState(state, 'actual'),
            approx1: approx1 - getState(state, 'approx1'),
            approx2: approx2 - getState(state, 'approx2'),
            approx3: approx3 - getState(state, 'approx3'),
            error3: error3 - getState(state, 'error3'),
        };
    },
    bounds: {
        x: { min: -2, max: 3 },
        actual: { min: -5, max: 20 },
    },
});
// ============================================================================
// DIFFERENTIAL EQUATION SOLUTIONS
// ============================================================================
export const DifferentialEquationSolutions = createExperimentFromSchema({
    id: 'differential-equation-solutions',
    name: 'Differential Equation Solutions',
    domain: 'mathematics',
    description: 'Explore solutions to first-order differential equations',
    conceptTags: ['Differential Equations', 'Separation of Variables', 'Integrating Factor', 'Slope Fields'],
    difficulty: 6,
    stateVariables: [
        { id: 't', name: 'Time t', unit: 's', initial: 0, symbol: 't' },
        { id: 'y', name: 'Solution y(t)', unit: 'dimensionless', initial: 1, symbol: 'y(t)' },
        { id: 'slope', name: 'dy/dt at point', unit: 'dimensionless/s', initial: -1, symbol: '\\frac{dy}{dt}' },
        { id: 'equilibrium', name: 'Equilibrium', unit: 'dimensionless', initial: 0, symbol: 'y^*' },
        { id: 'stable', name: 'Stable?', unit: 'dimensionless', initial: 1, symbol: 'S' },
    ],
    parameters: [
        { id: 'eqType', name: 'Equation Type', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'DE' },
        { id: 'y0', name: 'Initial y(0)', unit: 'dimensionless', min: 0.1, max: 10, default: 1, symbol: 'y_0' },
        { id: 'k', name: 'Rate Constant k', unit: '1/s', min: 0.1, max: 3, default: 1, symbol: 'k' },
    ],
    equations: [
        {
            id: 'separable-de',
            name: 'Separable DE',
            latex: '\\frac{dy}{dt} = ky, \\quad y(t) = y_0 e^{kt}',
            description: 'Exponential growth/decay',
        },
        {
            id: 'logistic-de',
            name: 'Logistic DE',
            latex: '\\frac{dy}{dt} = ky(1 - \\frac{y}{K})',
            description: 'Growth with carrying capacity',
        },
        {
            id: 'newton-cooling',
            name: 'Newton\'s Law of Cooling',
            latex: '\\frac{dy}{dt} = -k(y - T_{env})',
            description: 'Temperature approaches environment temperature',
        },
    ],
    derivatives: (state, params) => {
        const eqType = Math.round(getParam(params, 'eqType'));
        const y0 = getParam(params, 'y0');
        const k = getParam(params, 'k');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        let y = 0;
        let slope = 0;
        let equilibrium = 0;
        let stable = 1;
        switch (eqType) {
            case 0: // dy/dt = -ky (exponential decay)
                y = y0 * Math.exp(-k * nextT);
                slope = -k * y;
                equilibrium = 0;
                stable = 1;
                break;
            case 1: // dy/dt = k(1 - y) (Newton's cooling to 1)
                y = 1 + (y0 - 1) * Math.exp(-k * nextT);
                slope = k * (1 - y);
                equilibrium = 1;
                stable = 1;
                break;
            case 2: // dy/dt = y(1 - y) (logistic)
                y = 1 / (1 + ((1 - y0) / y0) * Math.exp(-k * nextT));
                slope = y * (1 - y);
                equilibrium = 1;
                stable = 1;
                break;
            case 3: // dy/dt = y (exponential growth)
                y = y0 * Math.exp(k * nextT);
                slope = k * y;
                equilibrium = 0;
                stable = 0; // Unstable at 0
                break;
            case 4: // Simple harmonic oscillator: dy/dt = v, dv/dt = -ω²y
                // Using y for position, slope is velocity
                const omega = 2;
                y = y0 * Math.cos(omega * nextT);
                slope = -y0 * omega * Math.sin(omega * nextT);
                equilibrium = 0;
                stable = 2; // Center (neutral stability)
                break;
        }
        return {
            t: 0.016,
            y: y - getState(state, 'y'),
            slope: slope - getState(state, 'slope'),
            equilibrium: equilibrium - getState(state, 'equilibrium'),
            stable: stable - getState(state, 'stable'),
        };
    },
    bounds: {
        t: { min: 0, max: 20 },
        y: { min: -5, max: 20 },
    },
});
// ============================================================================
// AREA BETWEEN CURVES
// ============================================================================
export const AreaBetweenCurves = createExperimentFromSchema({
    id: 'area-between-curves',
    name: 'Area Between Curves',
    domain: 'mathematics',
    description: 'Calculate area between two curves',
    conceptTags: ['Area Between Curves', 'Definite Integral', 'Intersection Points', 'Applications'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Position x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'f', name: 'f(x) upper', unit: 'dimensionless', initial: 4, symbol: 'f(x)' },
        { id: 'g', name: 'g(x) lower', unit: 'dimensionless', initial: 0, symbol: 'g(x)' },
        { id: 'difference', name: 'f(x) - g(x)', unit: 'dimensionless', initial: 4, symbol: 'f-g' },
        { id: 'totalArea', name: 'Total Area', unit: 'dimensionless', initial: 10.67, symbol: 'A' },
        { id: 'intersection1', name: 'Left Intersection', unit: 'dimensionless', initial: 0, symbol: 'x_1' },
        { id: 'intersection2', name: 'Right Intersection', unit: 'dimensionless', initial: 2, symbol: 'x_2' },
    ],
    parameters: [
        { id: 'pairType', name: 'Curve Pair', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'P' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.03, symbol: 'v' },
    ],
    equations: [
        {
            id: 'area-between',
            name: 'Area Between Curves',
            latex: 'A = \\int_a^b |f(x) - g(x)|\\,dx',
            description: 'Area equals integral of absolute difference',
        },
        {
            id: 'find-intersections',
            name: 'Finding Bounds',
            latex: 'f(x) = g(x) \\implies \\text{intersection points}',
            description: 'Solve for where curves intersect',
        },
    ],
    derivatives: (state, params) => {
        const pairType = Math.round(getParam(params, 'pairType'));
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let f = 0;
        let g = 0;
        let totalArea = 0;
        let i1 = 0;
        let i2 = 2;
        switch (pairType) {
            case 0: // y = x² and y = 4
                f = 4;
                g = nextX * nextX;
                i1 = -2;
                i2 = 2;
                // Area = ∫₋₂² (4 - x²) dx = [4x - x³/3]₋₂² = (8 - 8/3) - (-8 + 8/3) = 32/3
                totalArea = 32 / 3;
                break;
            case 1: // y = x and y = x²
                f = nextX;
                g = nextX * nextX;
                // Intersections at x = 0, 1
                i1 = 0;
                i2 = 1;
                // Area = ∫₀¹ (x - x²) dx = 1/6
                totalArea = 1 / 6;
                // f < g for x < 0 or x > 1
                if (nextX < 0 || nextX > 1) {
                    [f, g] = [g, f];
                }
                break;
            case 2: // y = 2x and y = x²
                f = 2 * nextX;
                g = nextX * nextX;
                i1 = 0;
                i2 = 2;
                // Area = ∫₀² (2x - x²) dx = [x² - x³/3]₀² = 4 - 8/3 = 4/3
                totalArea = 4 / 3;
                if (nextX < 0 || nextX > 2) {
                    [f, g] = [g, f];
                }
                break;
            case 3: // y = cos(x) and y = sin(x) on [0, π/2]
                f = Math.cos(nextX);
                g = Math.sin(nextX);
                i1 = 0;
                i2 = Math.PI / 4;
                // Area from 0 to π/4 where cos > sin
                totalArea = Math.sqrt(2) - 1;
                if (nextX > Math.PI / 4) {
                    [f, g] = [g, f];
                }
                break;
        }
        const diff = f - g;
        return {
            x: scanRate * 0.016,
            f: f - getState(state, 'f'),
            g: g - getState(state, 'g'),
            difference: diff - getState(state, 'difference'),
            totalArea: totalArea - getState(state, 'totalArea'),
            intersection1: i1 - getState(state, 'intersection1'),
            intersection2: i2 - getState(state, 'intersection2'),
        };
    },
    bounds: {
        x: { min: -3, max: 3 },
        f: { min: -5, max: 10 },
        totalArea: { min: 0, max: 20 },
    },
});
//# sourceMappingURL=calculus.js.map